import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Boxes, Package, CheckCircle, XCircle, Clock, Plus, Tag, 
  Edit, Trash2, DollarSign, Calendar, Filter, Building2
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

import { API } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';


const NA_SUPER_CATEGORIES = [
  "NA-Kitchen", "NA-Cafe", "NA-Bar", "NA-Service", "NA-Housekeeping", "NA-Packaging"
];

const getSuperCategoryColor = (sc) => {
  const colors = {
    "NA-Kitchen": "bg-orange-100 text-orange-800",
    "NA-Cafe": "bg-amber-100 text-amber-800",
    "NA-Bar": "bg-purple-100 text-purple-800",
    "NA-Service": "bg-blue-100 text-blue-800",
    "NA-Housekeeping": "bg-green-100 text-green-800",
    "NA-Packaging": "bg-pink-100 text-pink-800"
  };
  return colors[sc] || "bg-gray-100 text-gray-800";
};

export default function NAPriceMaster({ user }) {
  const [activeTab, setActiveTab] = useState('items');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [summary, setSummary] = useState({});
  
  // Filters
  const [filterSuperCategory, setFilterSuperCategory] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [viewMode, setViewMode] = useState('list'); // list, by-category
  
  // Dialogs
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  
  // Forms
  const [itemForm, setItemForm] = useState({
    item_name: '', super_category: '', category_id: '', company: '', unit: '', piece: '', vendor_id: ''
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', super_category: '' });
  const [priceForm, setPriceForm] = useState({ price: '' });
  
  const isAdmin = user?.role === 'admin';
  const isStorekeeper = user?.role === 'storekeeper';
  const canManage = isAdmin || isStorekeeper;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'ledger') {
      fetchPriceLedger();
    } else if (activeTab === 'pending' && isAdmin) {
      fetchPendingItems();
    } else {
      fetchItems();
    }
  }, [activeTab, filterSuperCategory, filterCategory, selectedMonth]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [categoriesRes, vendorsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/na-price-master/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/na-price-master/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCategories(categoriesRes.data);
      setVendors(vendorsRes.data);
      setSummary(summaryRes.data);
      
      await fetchItems();
      setLoading(false);
    } catch (error) {
      console.error('NA Price Master fetch error:', error);
      toast.error(error.message || 'Failed to fetch data', {
        action: {
          label: 'Retry',
          onClick: () => fetchData()
        }
      });
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { status: 'approved' };
      if (filterSuperCategory !== 'all') params.super_category = filterSuperCategory;
      if (filterCategory !== 'all') params.category_id = filterCategory;
      
      const response = await axios.get(`${API}/na-price-master/items`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    }
  };

  const fetchPriceLedger = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { month: selectedMonth };
      if (filterSuperCategory !== 'all') params.super_category = filterSuperCategory;
      if (filterCategory !== 'all') params.category_id = filterCategory;
      
      const response = await axios.get(`${API}/na-price-master/prices/ledger`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch price ledger');
    }
  };

  const fetchPendingItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/na-price-master/items/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingItems(response.data);
    } catch (error) {
      console.error('Failed to fetch pending items:', error);
    }
  };

  const handleCreateCategory = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/na-price-master/categories`, categoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category created');
      setShowAddCategory(false);
      setCategoryForm({ name: '', super_category: '' });
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create category'));
    }
  };

  const handleCreateItem = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/na-price-master/items`, itemForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isAdmin ? 'Item created and approved' : 'Item submitted for approval');
      setShowAddItem(false);
      setItemForm({ item_name: '', super_category: '', category_id: '', company: '', unit: '', piece: '', vendor_id: '' });
      fetchItems();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create item'));
    }
  };

  const handleApproveItem = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/na-price-master/items/${itemId}/approve`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item approved');
      fetchPendingItems();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve item'));
    }
  };

  const handleRejectItem = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/na-price-master/items/${itemId}/reject`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item rejected');
      fetchPendingItems();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject item'));
    }
  };

  const handleSavePrice = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/na-price-master/prices`, {
        item_id: editingPrice.id,
        month: selectedMonth,
        price: parseFloat(priceForm.price)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Price saved');
      setEditingPrice(null);
      setPriceForm({ price: '' });
      fetchPriceLedger();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save price'));
    }
  };

  const filteredCategories = filterSuperCategory === 'all' 
    ? categories 
    : categories.filter(c => c.super_category === filterSuperCategory);

  const groupedItems = filteredCategories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category_id === cat.id)
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="space-y-6" data-testid="na-price-master-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Boxes className="w-6 h-6 text-indigo-600" />
            NA-Price Master
          </h1>
          <p className="text-gray-600">Manage non-consumable assets pricing</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAddCategory(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
            <Button onClick={() => setShowAddItem(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-indigo-600" />
              <span className="text-sm text-indigo-600 font-medium">Total Items</span>
            </div>
            <p className="text-2xl font-bold text-indigo-700">{summary.total_items || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-600 font-medium">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">{summary.pending_items || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Tag className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 font-medium">Categories</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{summary.total_categories || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Prices Set ({summary.current_month})</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{summary.prices_set_this_month || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="ledger">Price Ledger</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="pending">
              Pending Approval
              {summary.pending_items > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                  {summary.pending_items}
                </Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <CardTitle>Approved Items</CardTitle>
                <div className="flex gap-2">
                  <Select value={filterSuperCategory} onValueChange={setFilterSuperCategory}>
                    <SelectTrigger className="w-44"><SelectValue placeholder="All Categories" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Super Categories</SelectItem>
                      {NA_SUPER_CATEGORIES.map(sc => (
                        <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="All Categories" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {filteredCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Boxes className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No items found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Super Category</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Vendor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>
                          <Badge className={getSuperCategoryColor(item.super_category)}>
                            {item.super_category}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.category_name}</TableCell>
                        <TableCell>{item.company}</TableCell>
                        <TableCell>{item.unit}{item.piece && ` (${item.piece})`}</TableCell>
                        <TableCell>{item.vendor_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Ledger Tab */}
        <TabsContent value="ledger" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <CardTitle>Price Ledger</CardTitle>
                <div className="flex gap-2 items-center">
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-40"
                  />
                  <Select value={filterSuperCategory} onValueChange={setFilterSuperCategory}>
                    <SelectTrigger className="w-44"><SelectValue placeholder="All Categories" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Super Categories</SelectItem>
                      {NA_SUPER_CATEGORIES.map(sc => (
                        <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={viewMode} onValueChange={setViewMode}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list">Price List</SelectItem>
                      <SelectItem value="by-category">By Category</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No items found</div>
              ) : viewMode === 'list' ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Super Category</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Price ({selectedMonth})</TableHead>
                      {canManage && <TableHead className="w-20">Action</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>
                          <Badge className={getSuperCategoryColor(item.super_category)}>
                            {item.super_category}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.category_name}</TableCell>
                        <TableCell>{item.company}</TableCell>
                        <TableCell>{item.vendor_name}</TableCell>
                        <TableCell className="text-right font-medium">
                          {item.current_price !== null ? formatCurrency(item.current_price) : 
                            <span className="text-gray-400">Not set</span>}
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingPrice(item);
                              setPriceForm({ price: item.current_price || '' });
                            }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="space-y-6">
                  {groupedItems.map((group) => (
                    <div key={group.id} className="border rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Badge className={getSuperCategoryColor(group.super_category)}>
                          {group.super_category}
                        </Badge>
                        {group.name}
                        <Badge variant="outline" className="ml-2">{group.items?.length || 0} items</Badge>
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item Name</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead>Vendor</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            {canManage && <TableHead className="w-20">Action</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(group.items || []).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{item.item_name}</TableCell>
                              <TableCell>{item.company}</TableCell>
                              <TableCell>{item.unit}</TableCell>
                              <TableCell>{item.vendor_name}</TableCell>
                              <TableCell className="text-right font-medium">
                                {item.current_price !== null ? formatCurrency(item.current_price) : 
                                  <span className="text-gray-400">Not set</span>}
                              </TableCell>
                              {canManage && (
                                <TableCell>
                                  <Button variant="ghost" size="sm" onClick={() => {
                                    setEditingPrice(item);
                                    setPriceForm({ price: item.current_price || '' });
                                  }}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approval Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending Approval</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>No pending items</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Name</TableHead>
                        <TableHead>Super Category</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Created By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell>
                            <Badge className={getSuperCategoryColor(item.super_category)}>
                              {item.super_category}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.category_name}</TableCell>
                          <TableCell>{item.company}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell>{item.vendor_name}</TableCell>
                          <TableCell>{item.created_by_name}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleApproveItem(item.id)} className="text-green-600 hover:text-green-700 hover:bg-green-50">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleRejectItem(item.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add NA Category</DialogTitle>
            <DialogDescription>Create a new category for non-consumable assets</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Super Category</Label>
              <Select value={categoryForm.super_category} onValueChange={(v) => setCategoryForm({ ...categoryForm, super_category: v })}>
                <SelectTrigger><SelectValue placeholder="Select super category" /></SelectTrigger>
                <SelectContent>
                  {NA_SUPER_CATEGORIES.map(sc => (
                    <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category Name</Label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="e.g., Utensils, Napkins" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategory(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} disabled={!categoryForm.name || !categoryForm.super_category}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add NA Item</DialogTitle>
            <DialogDescription>Add a new non-consumable asset item</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Super Category</Label>
              <Select value={itemForm.super_category} onValueChange={(v) => setItemForm({ ...itemForm, super_category: v, category_id: '' })}>
                <SelectTrigger><SelectValue placeholder="Select super category" /></SelectTrigger>
                <SelectContent>
                  {NA_SUPER_CATEGORIES.map(sc => (
                    <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={itemForm.category_id} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })} disabled={!itemForm.super_category}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.super_category === itemForm.super_category).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item Name</Label>
              <Input value={itemForm.item_name} onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })} placeholder="e.g., Stainless Steel Pan" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company/Brand</Label>
                <Input value={itemForm.company} onChange={(e) => setItemForm({ ...itemForm, company: e.target.value })} placeholder="e.g., Prestige" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} placeholder="e.g., piece, set" />
              </div>
            </div>
            <div>
              <Label>Size/Specification (optional)</Label>
              <Input value={itemForm.piece} onChange={(e) => setItemForm({ ...itemForm, piece: e.target.value })} placeholder="e.g., 12 inch, Large" />
            </div>
            <div>
              <Label>Vendor</Label>
              <Select value={itemForm.vendor_id} onValueChange={(v) => setItemForm({ ...itemForm, vendor_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleCreateItem} disabled={!itemForm.item_name || !itemForm.super_category || !itemForm.category_id || !itemForm.vendor_id}>
              {isAdmin ? 'Create & Approve' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Price Dialog */}
      <Dialog open={!!editingPrice} onOpenChange={() => setEditingPrice(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Price</DialogTitle>
            <DialogDescription>
              Set price for {editingPrice?.item_name} ({selectedMonth})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Price (₹)</Label>
              <Input type="number" step="0.01" value={priceForm.price} onChange={(e) => setPriceForm({ price: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPrice(null)}>Cancel</Button>
            <Button onClick={handleSavePrice} disabled={!priceForm.price}>Save Price</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
